import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
// Imported from the theme's own module: the aggregate prism barrel widens the style type
import CodeStyle from "react-syntax-highlighter/dist/cjs/styles/prism/coldark-dark";
import type { Components } from "react-markdown";
import ReactMarkdown from "react-markdown";
import type { CodeProps, TableDataCellProps, TableHeaderCellProps } from "react-markdown/lib/ast-to-react";
import remarkGfm from "remark-gfm";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";

import type { ReactNode } from "react";

// GitHub-flavoured Markdown: the question bank and the lesson texts are written with its tables and lists
const markdownPlugins = [ remarkGfm ];

const markdownComponents: Components = {
    // `node` is pulled out so it is not forwarded to the DOM <code> element
    code({ node, inline, className, children, ...props }: CodeProps) {
        const match = /language-(\w+)/.exec(className || '')
        return !inline && match ? (
            <SyntaxHighlighter
                style={ CodeStyle }
                language={ match[1] }
                PreTag="div"
            >
                { String(children).replace(/\n$/, '') }
            </SyntaxHighlighter>
        ) : (
            <code className={ className } { ...props }>
                { children }
            </code>
        )
    },

    // Tables come from the plugin as plain HTML, so they are mapped onto the MUI ones to match the theme
    table: ({ children }: { children?: ReactNode }) => (
        <TableContainer>
            <Table size="small">{ children }</Table>
        </TableContainer>
    ),
    thead: ({ children }: { children?: ReactNode }) => <TableHead>{ children }</TableHead>,
    tbody: ({ children }: { children?: ReactNode }) => <TableBody>{ children }</TableBody>,
    tr: ({ children }: { children?: ReactNode }) => <TableRow>{ children }</TableRow>,
    th: ({ children, style }: TableHeaderCellProps) => (
        <TableCell component="th" align={ alignOf(style) }>{ children }</TableCell>
    ),
    td: ({ children, style }: TableDataCellProps) => (
        <TableCell align={ alignOf(style) }>{ children }</TableCell>
    )
};

/** A column of a GFM table may be aligned with `:---:`, which the plugin reports as a text-align style. */
function alignOf(style?: Record<string, unknown>): 'left' | 'center' | 'right' {
    if (style?.textAlign === 'center') return 'center';
    if (style?.textAlign === 'right') return 'right';

    return 'left';
}

export type MarkdownProps = {
    text?: string;
    show?: boolean;
};

const Markdown = ({ text, show = true }: MarkdownProps) => {
    if (!show) return <></>;
    return <ReactMarkdown remarkPlugins={ markdownPlugins } components={ markdownComponents }>
        { text ?? '' }
    </ReactMarkdown>;
}

export default Markdown
